import {
  useEffect,
  useRef,
  useState,
} from 'react';
import './index.scss';
import MapInitService from '../../scripts/MapInitService';
import MapDrawService, { REGIONS } from '../../scripts/MapDrawService';
import MapApi from '../../scripts/MapApi';
import { debounce } from '../../scripts/utils';
import { useGlobalMapRenderPayload } from '../../scripts/useGlobalState';

const Map = ({ pageData }) => {
  const [isZooming, setIsZooming] = useState(false);
  const [isMoving, setIsMoving] = useState(false);
  const mymap = useRef(null);
  const mapDrawServiceRef = useRef(null);
  const mapApiRef = useRef(null);
  const mapRenderPayload = useGlobalMapRenderPayload();

  // Keep body/html overflow-hidden while the user is panning or zooming
  useEffect(() => {
    const body = document.querySelector('body');
    const html = document.querySelector('html');
    
    [html, body].forEach((el) => {
      if (isZooming || isMoving) {
        el.classList.add('overflow-hidden');
      } else {
        el.classList.remove('overflow-hidden');
      }
    });
  }, [isZooming, isMoving]);

  // Initialise Leaflet map once on mount
  useEffect(() => {
    const handleMapMove = (addHiddenClass) => setIsMoving(addHiddenClass);
    const handleZoom = (addHiddenClass) => setIsZooming(addHiddenClass);

    mymap.current = new MapInitService().initMap();
    mymap.current.on('movestart', () => handleMapMove(true));
    mymap.current.on('moveend', () => handleMapMove(false));
    mymap.current.on('zoomstart', () => handleZoom(true));
    mymap.current.on('zoomend', debounce(() => handleZoom(false), 400));

    mapDrawServiceRef.current = new MapDrawService(null, mymap.current, null);

    return () => {
      mymap.current.off('movestart');
      mymap.current.off('moveend');
      mymap.current.off('zoomstart');
      mymap.current.off('zoomend');
    };
  }, []);

  // Keep MapApi in sync with the current page
  useEffect(() => {
    mapApiRef.current = new MapApi(pageData);

    if (mapDrawServiceRef.current) {
      mapDrawServiceRef.current.setMapApi(mapApiRef.current);
    }
  }, [pageData]);

  // React to data dispatched by the central orchestrator and render Leaflet layers
  useEffect(() => {
    if (!mapRenderPayload || !mapDrawServiceRef.current) return;

    const {
      renderMode,
      region,
      selectedYear,
      categoryTooltip,
      markerIcon,
      tooltipKey,
      statesGeoJson,
      enrichedLandkreiseGeoJson,
      region2Geos,
      ranks,
    } = mapRenderPayload;

    const mapDrawService = mapDrawServiceRef.current;
    mapDrawService.setSelectedYear(selectedYear);
    mapDrawService.setCategory(categoryTooltip);
    mapDrawService.setTooltipKey(tooltipKey);

    if (renderMode === 'full') {
      mapDrawService.clearMap();

      if (!region || region === 0) {
        if (statesGeoJson) {
          mapDrawService.renderGeoJsonsPolygons(REGIONS.STATES, statesGeoJson);
        }
        if (ranks) {
          mapDrawService.updateRankOfPolygons(ranks.data);
        }
      } else if (region === 1) {
        if (enrichedLandkreiseGeoJson) {
          mapDrawService.renderGeoJsonsPolygons(REGIONS.DISTRICS, enrichedLandkreiseGeoJson);
        }
      } else if (region === 2) {
        if (region2Geos) {
          mapDrawService.renderGeoJsonsMarkers(region, markerIcon, { features: region2Geos });
        }
      }
    } else if (renderMode === 'ranks-only') {
      if (!region || region === 0) {
        mapDrawService.resetAllTooltipsForPolygon(REGIONS.STATES);
        if (ranks) {
          mapDrawService.updateRankOfPolygons(ranks.data);
        }
      } else if (region === 1) {
        mapDrawService.resetAllTooltipsForPolygon(REGIONS.DISTRICS);
        if (ranks) {
          mapDrawService.updateRankOfPolygons(ranks.data);
        }
      } else if (region === 2) {
        // Markers have no year-based colouring — re-render them as-is
        mapDrawService.clearMap();
        if (region2Geos) {
          mapDrawService.renderGeoJsonsMarkers(region, markerIcon, { features: region2Geos });
        }
      }
    }
  }, [mapRenderPayload]);

  return <div id="mapid" />;
};

export default Map;
